import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Platform} from 'react-native';
import {useSelector, useDispatch} from 'react-redux';
import Loading from 'components/World/Loading';
import {resolveToken} from 'services/LessonContentService';
import {LESSON_FRONTEND_URL} from 'config/app.config';
import {
  fetchLessonContentAction,
  advancePhase,
  setTutorialViewed,
  setWorkbookReady,
  setWorkbookDone,
  submitWorkbookAnswer,
  submitKCAnswer,
  advanceKCQuestion,
  kcSummaryDone,
  submitMasteryAnswer,
  advanceMasteryQuestion,
  setTutorChatOpen,
  jumpToDevTimelineNode,
  resetViewer,
} from 'store/lessonContentViewer/actions';
import {
  selectViewerLoading,
  selectViewerError,
  selectViewerModel,
  selectCurrentPhase,
  selectActiveTerm,
  selectActiveTermIndex,
  selectActiveQuestionIndex,
  selectWorkbookState,
  selectActiveKCQuestion,
  selectKCAnswers,
  selectKCScore,
  selectActiveMasteryQuestion,
  selectMasteryAnswers,
  selectMasteryScore,
  selectMasteryComplete,
  selectProgressTrackerData,
  selectTutorText,
  selectShowDevTools,
  selectTutorChatOpen,
  selectFrontloadingData,
  selectMetadata,
  selectMasteryQuestions,
  selectLessonViewerUnityWorldLoading,
} from 'store/lessonContentViewer/selectors';
import LCVFrontloadingStage from './components/LCVFrontloadingStage';
import LCVStaticLessonStage from './components/LCVStaticLessonStage';
import LCVActionSidebar from './components/LCVActionSidebar';
import LCVDevStrip from './components/LCVDevStrip';
import TutorialPhase from './components/TutorialPhase';
import LearningPhase from './components/LearningPhase';
import WorkbookReadyPhase from './components/WorkbookReadyPhase';
import WorkbookOptionsPhase from './components/WorkbookOptionsPhase';
import KnowledgeCheckPhase from './components/KnowledgeCheckPhase';
import KCSummaryPhase from './components/KCSummaryPhase';
import MasteryPhase from './components/MasteryPhase';
import ResultsPhase from './components/ResultsPhase';
import LessonViewerUnityBackground from './LessonViewerUnityBackground';
import './LessonContentViewer.css';

/** Opaque black behind the progress bar so Unity / lesson UI do not show through. */
const LCV_UNITY_LOADING_BG = '#000000';
/** ~1%/s default is too slow for long Unity init; keep bar moving toward cap while overlay is up. */
const LCV_UNITY_LOADING_FAKE_RATE_PER_SEC = 9;

const LessonContentViewer = () => {
  const dispatch = useDispatch();

  const loading = useSelector(selectViewerLoading);
  const error = useSelector(selectViewerError);
  const viewerModel = useSelector(selectViewerModel);
  const phase = useSelector(selectCurrentPhase);
  const activeTerm = useSelector(selectActiveTerm);
  const activeTermIndex = useSelector(selectActiveTermIndex);
  const activeQuestionIndex = useSelector(selectActiveQuestionIndex);
  const workbookState = useSelector(selectWorkbookState);
  const activeKCQuestion = useSelector(selectActiveKCQuestion);
  const kcAnswers = useSelector(selectKCAnswers);
  const kcScore = useSelector(selectKCScore);
  const activeMasteryQuestion = useSelector(selectActiveMasteryQuestion);
  const masteryAnswers = useSelector(selectMasteryAnswers);
  const masteryScore = useSelector(selectMasteryScore);
  const masteryComplete = useSelector(selectMasteryComplete);
  const masteryQuestions = useSelector(selectMasteryQuestions);
  const progressData = useSelector(selectProgressTrackerData);
  const tutorText = useSelector(selectTutorText);
  const showDevTools = useSelector(selectShowDevTools);
  const tutorChatOpen = useSelector(selectTutorChatOpen);
  const frontloadingData = useSelector(selectFrontloadingData);
  const metadata = useSelector(selectMetadata);
  const unityWorldLoading = useSelector(selectLessonViewerUnityWorldLoading);
  /**
   * Stays true after Redux clears `unityWorldLoading` until `Loading` finishes its exit animation.
   * Updated synchronously during render (no useEffect gap) so `Loading` never unmounts/remounts
   * and fake progress does not reset to a new random %.
   */
  const unityOverlayLockedRef = useRef(false);
  const [, bumpUnityOverlay] = useState(0);
  const [visualContent, setVisualContent] = useState(null);
  const [trackerMode, setTrackerMode] = useState('mini');

  const authToken = useMemo(() => resolveToken(), []);

  const lessonId = useMemo(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      return params.get('lessonId') || 'G3-MATH-L001-720L';
    }
    return 'G3-MATH-L001-720L';
  }, []);

  useEffect(() => {
    if (!authToken) return;
    dispatch(fetchLessonContentAction(lessonId, authToken));
    return () => dispatch(resetViewer());
  }, [dispatch, lessonId, authToken]);

  useEffect(() => {
    setVisualContent(null);
  }, [lessonId]);

  useEffect(() => {
    unityOverlayLockedRef.current = false;
    bumpUnityOverlay(n => n + 1);
  }, [lessonId]);

  const onUnityLoadingExitAnimationComplete = useCallback(() => {
    unityOverlayLockedRef.current = false;
    bumpUnityOverlay(n => n + 1);
  }, []);

  useEffect(() => {
    if (phase === 'frontloading') {
      setTrackerMode('full');
      return undefined;
    }

    if (phase === 'knowledge_check_summary') {
      setTrackerMode('full');
      const collapseTimer = window.setTimeout(() => {
        setTrackerMode('mini');
      }, 5000);
      return () => window.clearTimeout(collapseTimer);
    }

    setTrackerMode('mini');
    return undefined;
  }, [phase]);

  const showShell =
    phase !== 'tutorial' && !loading && !error && viewerModel;
  const useFrontloadingStage =
    showShell && phase === 'frontloading' && progressData && frontloadingData;
  const useStaticStage =
    showShell && phase !== 'frontloading';

  if (unityWorldLoading) {
    unityOverlayLockedRef.current = true;
  }

  const showUnityLoadingOverlay =
    Platform.OS === 'web' &&
    (unityWorldLoading || unityOverlayLockedRef.current);

  const showMetaverse = [
    'frontloading',
    'learning',
    'workbook_ready',
    'workbook_exercise',
    'workbook_options',
    'knowledge_check',
    'knowledge_check_summary',
  ].includes(phase);

  if (!authToken) {
    return (
      <div className="lcv-root lcv-auth-gate">
        <div className="lcv-auth-modal">
          <div className="lcv-auth-icon">🔒</div>
          <h2 className="lcv-auth-title">Access Required</h2>
          <p className="lcv-auth-message">
            This lesson is not available at this address.
            Please use the link provided to you to access your lesson.
          </p>
          <a
            href={LESSON_FRONTEND_URL}
            className="lcv-btn-primary lcv-auth-link"
            rel="noopener noreferrer">
            Go to QuikLearn
          </a>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="lcv-root">
        <div className="lcv-loading">
          <div className="lcv-spinner" />
          <p>Loading lesson...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="lcv-root">
        <div className="lcv-error">
          <span className="lcv-error-icon">!</span>
          <h2>Unable to load lesson</h2>
          <p>{error}</p>
          <button
            className="lcv-btn-primary"
            onClick={() => dispatch(fetchLessonContentAction(lessonId))}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!viewerModel) return null;

  return (
    <div className={`lcv-root ${tutorChatOpen ? 'lcv-tutor-active' : ''}`}>
      <LessonViewerUnityBackground />

      {showUnityLoadingOverlay && (
        <div
          className="lcv-unity-world-loading"
          aria-busy={unityWorldLoading}
          aria-live="polite">
          <Loading
            loading={unityWorldLoading}
            color={LCV_UNITY_LOADING_BG}
            label="Loading world…"
            fakeRatePerSecond={LCV_UNITY_LOADING_FAKE_RATE_PER_SEC}
            onExitAnimationComplete={onUnityLoadingExitAnimationComplete}
          />
        </div>
      )}

      {showShell && (
        <LCVActionSidebar
          tutorChatOpen={tutorChatOpen}
          onToggleTutorChat={() => dispatch(setTutorChatOpen(!tutorChatOpen))}
        />
      )}

      {phase === 'tutorial' && (
        <TutorialPhase onComplete={() => dispatch(setTutorialViewed())} />
      )}

      {useFrontloadingStage && (
        <LCVFrontloadingStage
          data={frontloadingData}
          metadata={metadata}
          progressData={progressData}
          phase={phase}
          trackerMode={trackerMode}
          visualContent={visualContent}
          onStart={() => dispatch(advancePhase())}
          paused={tutorChatOpen}
        />
      )}

      {useStaticStage && (
        <LCVStaticLessonStage
          metadata={metadata}
          progressData={progressData}
          phase={phase}
          activeTerm={activeTerm}
          tutorText={tutorText}
          trackerMode={trackerMode}
          visualContent={visualContent}
          middleContent={
            <>
              {phase === 'learning' && activeTerm && (
                <LearningPhase
                  term={activeTerm}
                  termIndex={activeTermIndex}
                  onContinue={() => dispatch(advancePhase())}
                />
              )}

              {(phase === 'workbook_ready' || phase === 'workbook_exercise') &&
                activeTerm && (
                  <WorkbookReadyPhase
                    term={activeTerm}
                    workbookState={workbookState}
                    phase={phase}
                    onReady={() => dispatch(setWorkbookReady())}
                    onDone={() => dispatch(setWorkbookDone())}
                  />
                )}

              {phase === 'workbook_options' && activeTerm && (
                <WorkbookOptionsPhase
                  term={activeTerm}
                  workbookState={workbookState}
                  onSubmit={idx => dispatch(submitWorkbookAnswer(idx))}
                  onContinue={() => dispatch(advancePhase())}
                />
              )}

              {phase === 'knowledge_check' && activeKCQuestion && (
                <KnowledgeCheckPhase
                  question={activeKCQuestion}
                  questionIndex={activeQuestionIndex}
                  totalQuestions={activeTerm?.knowledgeCheck?.questions?.length || 0}
                  kcAnswers={kcAnswers}
                  onSubmit={answer => dispatch(submitKCAnswer(answer))}
                  onNext={() => dispatch(advanceKCQuestion())}
                />
              )}

              {phase === 'knowledge_check_summary' && (
                <KCSummaryPhase
                  score={kcScore}
                  termName={activeTerm?.term}
                  onContinue={() => dispatch(kcSummaryDone())}
                />
              )}

              {phase === 'mastery' && activeMasteryQuestion && (
                <MasteryPhase
                  question={activeMasteryQuestion}
                  questionIndex={activeQuestionIndex}
                  totalQuestions={masteryQuestions.length}
                  masteryAnswers={masteryAnswers}
                  onSubmit={answer => dispatch(submitMasteryAnswer(answer))}
                  onNext={() => dispatch(advanceMasteryQuestion())}
                />
              )}

              {phase === 'results' && (
                <ResultsPhase
                  score={masteryScore}
                  terms={viewerModel.terms}
                  masteryAnswers={masteryAnswers}
                />
              )}
            </>
          }
        />
      )}

      {showDevTools && viewerModel && (
        <LCVDevStrip
          metadata={metadata}
          phase={phase}
          activeTermIndex={activeTermIndex}
          activeQuestionIndex={activeQuestionIndex}
          terms={viewerModel.terms}
          masteryQuestionCount={masteryQuestions.length}
          onJump={node => dispatch(jumpToDevTimelineNode(node))}
        />
      )}
    </div>
  );
};

export default LessonContentViewer;
